import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { AGENT_SEVERITIES, AGENT_SEVERITY_LABELS } from '@padcanvas/shared';
import { adminApi, type AgentFeedback } from '../../api/adminApi';
import { Badge, DataTable, type ColumnDef, type SortState } from '@shared-ui/components';
import { sevVariant } from './severityBadge';
import { TriageDialog } from './TriageDialog';
import { SOURCES, SOURCE_LABEL, SOURCE_VARIANT } from './findingSource';

const STATUSES = ['new', 'triaged', 'resolved', 'dismissed'];
const CATEGORIES = ['ux', 'pedagogy', 'correctness', 'latency', 'confusion', 'praise', 'bug'];
// Superset across apps — mirrors the backend FeedbackOperationSchema (agents/agentTypes.ts).
const OPERATIONS = [
    // student app
    'solution-analysis', 'hint', 'concept-lab', 'daily-session', 'question-list', 'practice', 'first-run', 'plan-goals', 'home', 'test-assignment',
    // teacher app
    'attempt-review', 'scoring', 'student-progress', 'class-overview', 'assignments',
    // shared
    'navigation', 'other',
];

/** Column key → comparable value, so client-side sort matches what each column shows. */
function sortValue(f: AgentFeedback, key: string): string | number {
  switch (key) {
    case 'date': return f.createdAt ? new Date(f.createdAt).getTime() : 0;
    case 'persona': return f.personaId;
    case 'operation': return f.operation ?? '';
    case 'type': return f.category;
    case 'sev': return f.triageSeverity ?? f.severity;
    case 'source': return f.source ?? '';
    case 'status': return f.status ?? 'new';
    default: return 0;
  }
}

export function FindingsTab() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [category, setCategory] = useState('');
  const [operation, setOperation] = useState('');
  const [maxSev, setMaxSev] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState>({ sortBy: 'date', sortOrder: 'desc' });
  // Finding being triaged (dialog open), plus the status the admin just picked for it.
  const [triage, setTriage] = useState<{ finding: AgentFeedback; status?: string } | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['agent-findings', status, source, category, operation, maxSev],
    queryFn: () => adminApi.getAgentFindings({
      status: status || undefined,
      source: source || undefined,
      category: category || undefined,
      operation: operation || undefined,
      maxSeverity: maxSev ? Number(maxSev) : undefined,
    }),
  });

  const findings = useMemo(() => {
    let rows = data?.findings ?? [];
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(f =>
        f.personaId.toLowerCase().includes(q) || (f.observation ?? '').toLowerCase().includes(q));
    }
    const dir = sort.sortOrder === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = sortValue(a, sort.sortBy);
      const vb = sortValue(b, sort.sortBy);
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return 0;
    });
  }, [data, search, sort]);

  const columns = useMemo<ColumnDef<AgentFeedback>[]>(() => [
    { key: 'date', header: 'Date', sortable: true, className: 'pa-num', render: f => (f.createdAt ? format(new Date(f.createdAt), 'MMM d HH:mm') : '—') },
    { key: 'persona', header: 'Agent', sortable: true, render: f => <span className="pa-mono">{f.personaId}</span> },
    { key: 'operation', header: 'Operation', sortable: true, render: f => (f.operation ? <Badge variant="info" size="sm">{f.operation}</Badge> : '—') },
    { key: 'type', header: 'Type', sortable: true, render: f => <Badge variant="default" size="sm">{f.category}</Badge> },
    {
      // Human triage severity wins when set; the agent's auto value shows struck-through
      // beside it so the override is legible.
      key: 'sev', header: 'Sev (1=critical)', sortable: true, className: 'pa-num',
      render: f => {
        const eff = f.triageSeverity ?? f.severity;
        return (
          <span className="pa-sev-cell">
            <Badge variant={sevVariant(eff)} size="sm">{eff}</Badge>
            {f.triageSeverity != null && f.triageSeverity !== f.severity && (
              <span className="pa-sev-auto" title="agent's auto severity">{f.severity}</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'source', header: 'Source', sortable: true,
      render: f => (f.source ? <Badge variant={SOURCE_VARIANT[f.source]} size="sm">{SOURCE_LABEL[f.source]}</Badge> : <span className="pa-dim">—</span>),
    },
    { key: 'obs', header: 'Observation', render: f => <span className="pa-clip">{f.observation}</span> },
    {
      // Status change routes through the triage dialog so a comment/attribution is always
      // captured. The select opening the dialog (rather than committing inline) keeps the
      // "pick a status → tell me why" flow the reviewer expects.
      key: 'status', header: 'Status', sortable: true, render: f => (
        <div className="pa-status-cell" onClick={e => e.stopPropagation()}>
          <select
            className="pa-input pa-status"
            value={f.status ?? 'new'}
            onChange={e => setTriage({ finding: f, status: e.target.value })}
          >
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button
            type="button"
            className={`pa-triage-note ${(f.triageComment || f.triageLog?.length) ? 'has-note' : ''}`}
            title={f.triageComment ? `Latest: ${f.triageComment}` : 'Triage / add note'}
            onClick={() => setTriage({ finding: f })}
          >
            {f.substatus ? f.substatus : '💬'}
          </button>
        </div>
      ),
    },
  ], []);

  return (
    <>
      <div className="pa-filters">
        <input
          className="pa-input pa-search"
          type="search"
          placeholder="Search agent or observation…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="pa-input" value={operation} onChange={e => setOperation(e.target.value)}>
          <option value="">All operations</option>
          {OPERATIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <select className="pa-input" value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">All types</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="pa-input" value={maxSev} onChange={e => setMaxSev(e.target.value)}>
          <option value="">Any severity</option>
          {/* Filters to "at least this severe" — severity ≤ n, since 1 is the critical end. */}
          {AGENT_SEVERITIES.map(n => <option key={n} value={n}>{n} ({AGENT_SEVERITY_LABELS[n]}) or worse</option>)}
        </select>
        <select className="pa-input" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="pa-input" value={source} onChange={e => setSource(e.target.value)}>
          <option value="">Any source</option>
          {SOURCES.map(s => <option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
        </select>
      </div>
      <DataTable<AgentFeedback>
        columns={columns}
        data={findings}
        rowKey={f => f.id}
        onRowClick={f => navigate(`/persona-agents/runs/${encodeURIComponent(f.runId)}`)}
        loading={isLoading}
        emptyMessage="No findings"
        sort={sort}
        onSortChange={setSort}
      />
      {triage && (
        <TriageDialog
          finding={triage.finding}
          initialStatus={triage.status}
          onClose={() => setTriage(null)}
          onSaved={() => {
            setTriage(null);
            qc.invalidateQueries({ queryKey: ['agent-findings'] });
          }}
        />
      )}
    </>
  );
}
